import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Icon,
  useTheme,
  alpha,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import { OrderResponse } from "../../types/pos.types";
import { formatCurrency } from "../../utils/formatters";
import moment from "moment";

interface OrdersListProps {
  orders: OrderResponse[];
  loading: boolean;
  onVoidOrder: (orderId: string, reason: string) => void;
  onContinueOrder: (order: OrderResponse) => void;
  onUpdateOrder: (order: OrderResponse) => void;
  is_pos_supervisor: boolean;
}

export const OrdersList: React.FC<OrdersListProps> = ({
  orders,
  loading,
  onVoidOrder,
  onContinueOrder,
  onUpdateOrder,
  is_pos_supervisor,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedOrder, setSelectedOrder] =
    React.useState<OrderResponse | null>(null);
  const [selectedOrderForVoid, setSelectedOrderForVoid] =
    React.useState<OrderResponse | null>(null);
  const [voidReason, setVoidReason] = React.useState("");
  const [isVoiding, setIsVoiding] = React.useState(false);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    order: OrderResponse
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrder(null);
  };

  const handleVoidClick = (order: OrderResponse) => {
    setSelectedOrderForVoid(order);
    setVoidReason("");
  };

  const handleVoidConfirm = async () => {
    if (selectedOrderForVoid && voidReason.trim()) {
      setIsVoiding(true);
      try {
        await onVoidOrder(selectedOrderForVoid.order_id, voidReason.trim());
      } catch (error) {
        console.error("Error voiding order:", error);
      } finally {
        setIsVoiding(false);
        setSelectedOrderForVoid(null);
        setVoidReason("");
      }
    }
  };

  const canVoidOrder = (order: OrderResponse) => {
    return order.status === "on_hold" || order.status === "pending_payment";
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return {
          color: "#059669",
          backgroundColor: "#ECFDF5",
          borderColor: "#A7F3D0"
        };
      case "pending_payment":
        return {
          color: "#D97706",
          backgroundColor: "#FFFBEB",
          borderColor: "#FCD34D"
        };
      case "on_hold":
        return {
          color: "#2563EB",
          backgroundColor: "#EFF6FF",
          borderColor: "#93C5FD"
        };
      case "voided":
        return {
          color: "#DC2626",
          backgroundColor: "#FEF2F2",
          borderColor: "#FECACA"
        };
      default:
        return {
          color: "#4B5563",
          backgroundColor: "#F3F4F6",
          borderColor: "#D1D5DB"
        };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "check_circle";
      case "pending_payment":
        return "pending";
      case "on_hold":
        return "pause_circle";
      case "voided":
        return "cancel";
      default:
        return "help";
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case "cash":
        return "payments";
      case "card":
      case "mastercard":
        return "credit_card";
      case "mobile":
      case "benefitpay":
        return "phone_android";
      default:
        return "payment";
    }
  };

  const formatPaymentMethod = (method: string) => {
    switch (method.toLowerCase()) {
      case "mastercard":
        return "CARD";
      case "benefitpay":
        return "MOBILE";
      default:
        return method.toUpperCase();
    }
  };

  const getVoidButtonTooltip = (order: OrderResponse) => {
    if (!is_pos_supervisor) {
      return "Only POS supervisors can void orders";
    }
    if (order.status === "completed") {
      return "Completed orders cannot be voided";
    }
    if (order.status === "voided") {
      return "This order has already been voided";
    }
    return "Void Order";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (orders.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          gap: 2,
          p: 3,
        }}
      >
        <Icon sx={{ fontSize: 48, color: "text.secondary" }}>receipt_long</Icon>
        <Typography variant="h6" color="text.secondary">
          No Orders Found
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Create a new order to get started
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer sx={{ height: "100%" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
              <TableCell>Date & Time</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Subtotal</TableCell>
              <TableCell>Tax</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order, index) => (
              <TableRow
                key={order.order_id}
                sx={{
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {index + 1}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {moment(order.order_date).format("DD/MM/YY")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {moment(order.order_date).format("HH:mm")}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {order.items.length} items
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.items
                        .map((item: { name: string }) => item.name)
                        .join(", ")}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon sx={{ fontSize: 16 }}>
                      {getPaymentMethodIcon(order.payment.method)}
                    </Icon>
                    <Typography variant="body2">
                      {formatPaymentMethod(order.payment.method)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatCurrency(order.raw_price)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(order.tax_amount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(order.total_price)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={
                      <Icon sx={{ 
                        fontSize: 18,
                        color: getStatusColor(order.status).color,
                        marginLeft: "2px"
                      }}>
                        {getStatusIcon(order.status)}
                      </Icon>
                    }
                    label={order.status.replace(/_/g, " ")}
                    size="small"
                    sx={{
                      minWidth: 100,
                      height: "32px",
                      color: getStatusColor(order.status).color,
                      backgroundColor: getStatusColor(order.status).backgroundColor,
                      border: "1px solid",
                      borderColor: getStatusColor(order.status).borderColor,
                      borderRadius: "16px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: getStatusColor(order.status).backgroundColor,
                        transform: "translateY(-1px)",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      },
                      "& .MuiChip-label": {
                        px: 1.5,
                        paddingLeft: "6px",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        textTransform: "capitalize",
                        letterSpacing: "0.01em"
                      }
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Box
                    sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                  >
                    {order.status === "on_hold" && (
                      <Tooltip title="Continue Order">
                        <IconButton
                          size="small"
                          onClick={() => onContinueOrder(order)}
                          sx={{
                            color: theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.1
                              ),
                            },
                          }}
                        >
                          <Icon>play_arrow</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                    {order.status === "pending_payment" && (
                      <Tooltip title="Update Order">
                        <IconButton
                          size="small"
                          onClick={() => onUpdateOrder(order)}
                          sx={{
                            color: theme.palette.info.main,
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.info.main,
                                0.1
                              ),
                            },
                          }}
                        >
                          <Icon>edit</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                    {is_pos_supervisor && (
                      <Tooltip title={getVoidButtonTooltip(order)}>
                        <span>
                          <Button
                            size="small"
                            onClick={() => handleVoidClick(order)}
                            // disabled={!canVoidOrder(order)}
                            startIcon={<Icon>block</Icon>}
                            variant="outlined"
                            color="error"
                            sx={{
                              minWidth: "auto",
                              px: 1.5,
                              py: 0.5,
                              color: canVoidOrder(order)
                                ? theme.palette.error.main
                                : theme.palette.text.disabled,
                              borderColor: canVoidOrder(order)
                                ? theme.palette.error.main
                                : theme.palette.divider,
                              "&:hover": {
                                backgroundColor: canVoidOrder(order)
                                  ? alpha(theme.palette.error.main, 0.1)
                                  : "transparent",
                                borderColor: canVoidOrder(order)
                                  ? theme.palette.error.main
                                  : theme.palette.divider,
                              },
                              "&.Mui-disabled": {
                                color: theme.palette.text.disabled,
                                borderColor: theme.palette.divider,
                              },
                            }}
                          >
                            Void
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Void Order Confirmation Dialog */}
      <Dialog
        open={!!selectedOrderForVoid}
        onClose={() => setSelectedOrderForVoid(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <DialogTitle sx={{ color: theme.palette.error.main }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon>warning</Icon>
            Void Order
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to void this order? This action cannot be
            undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Order ID: {selectedOrderForVoid?.order_id}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Total Amount: BHD {selectedOrderForVoid?.total_price.toFixed(3)}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Void"
            fullWidth
            multiline
            rows={3}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="Please provide a reason for voiding this order..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setSelectedOrderForVoid(null)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleVoidConfirm}
            variant="contained"
            color="error"
            disabled={!voidReason.trim() || isVoiding}
            startIcon={
              isVoiding ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon>block</Icon>
              )
            }
          >
            {isVoiding ? "Voiding..." : "Void Order"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
